<template>
  <div class="sp-split">
    <CSLoading :is-loading="saving"></CSLoading>

    <!-- Hinweise: Dokumente bleiben am Ursprung, Betraege werden 1:1 kopiert -->
    <div class="sp-split__hints">
      <p><md-icon>info</md-icon> {{ $t("shippingPlan.split.docsHint") }}</p>
      <p><md-icon>info</md-icon> {{ $t("shippingPlan.split.amountsHint") }}</p>
    </div>

    <!-- Gemeinsamer Scrollbereich: alle Spalten bewegen sich zusammen -->
    <div class="sp-split__scroll">
      <section v-for="(col, index) in columns" :key="index" class="sp-split__col" :class="{ 'sp-split__col--origin': index === 0 }">
        <h4>
          {{ index === 0 ? $t("shippingPlan.split.origin") : $t("shippingPlan.split.container", { n: index + 1 }) }}
          <md-button v-if="index > 0" class="md-icon-button md-simple sp-split__remove" :title="$t('shippingPlan.split.remove')" @click="removeColumn(index)">
            <md-icon>close</md-icon>
          </md-button>
        </h4>

        <template v-for="field in fields">
          <md-field v-if="field.type === 'text'" :key="field.key">
            <label>{{ $t(field.label) }}</label>
            <md-input v-model="col[field.key]" type="text"></md-input>
          </md-field>

          <md-field v-else-if="field.type === 'textarea'" :key="field.key">
            <label>{{ $t(field.label) }}</label>
            <md-textarea v-model="col[field.key]" md-autogrow></md-textarea>
          </md-field>

          <md-field v-else-if="field.type === 'money' || field.type === 'number'" :key="field.key">
            <label>{{ $t(field.label) }}</label>
            <md-input v-model="col[field.key]" type="text" inputmode="decimal"></md-input>
          </md-field>

          <date-picker v-else-if="field.type === 'date'" :key="field.key" v-model="col[field.key]" format="YYYY-MM-DD" type="date" :placeholder="$t(field.label)" class="sp-split__date"></date-picker>

          <div v-else-if="field.type === 'datetime'" :key="field.key" class="sp-split__datetime">
            <span class="v-select-label">{{ $t(field.label) }}</span>
            <date-time-picker :startDate="col[field.key]" :singleDate="true" timeFormat="HH:mm" class="datetime-input" @onChange="(d) => (col[field.key] = d.startDate)" />
          </div>

          <div v-else-if="field.type === 'select'" :key="field.key" class="wrap-v-select sp-split__select">
            <span class="v-select-label">{{ $t(field.label) }}</span>
            <v-select
              class="custom-select"
              :options="options[field.options] || []"
              :getOptionLabel="(o) => optionLabel(field, o)"
              :reduce="(o) => o.id"
              :value="col[field.key]"
              :append-to-body="true"
              @input="col[field.key] = $event"
            >
              <!-- gleiche Darstellung wie im Bearbeiten-Dialog: farbiger Chip beim Status,
                   Logo bei Logistiker/Anlieferstelle, Avatar beim China-Benutzer -->
              <template #option="option">
                <md-chip v-if="option.color" :style="`background-color: ${option.color} !important; color: #fff !important`">{{ optionLabel(field, option) }}</md-chip>
                <span v-else>
                  <img v-if="option.logo" :src="option.logo" class="sp-split__option-logo" />
                  <user-avatar v-else-if="field.key === 'chinaUser'" :user-id="option.id" class="sp-split__option-avatar" />
                  {{ optionLabel(field, option) }}
                </span>
              </template>
              <template #selected-option="option">
                <md-chip v-if="option.color" :style="`background-color: ${option.color} !important; color: #fff !important`">{{ optionLabel(field, option) }}</md-chip>
                <span v-else>
                  <img v-if="option.logo" :src="option.logo" class="sp-split__option-logo" />
                  <user-avatar v-else-if="field.key === 'chinaUser'" :user-id="option.id" class="sp-split__option-avatar" />
                  {{ optionLabel(field, option) }}
                </span>
              </template>
            </v-select>
          </div>

          <md-field v-else-if="field.type === 'mdselect'" :key="field.key">
            <label>{{ $t(field.label) }}</label>
            <md-select v-model="col[field.key]">
              <md-option v-for="o in options[field.options] || []" :key="o.id" :value="o.id">{{ optionLabel(field, o) }}</md-option>
            </md-select>
          </md-field>

          <md-checkbox v-else-if="field.type === 'bool'" :key="field.key" v-model="col[field.key]">{{ $t(field.label) }}</md-checkbox>
        </template>

        <!-- Ankunftsdatum-Typ wie im Bearbeiten-Dialog: genau einer von drei -->
        <div class="wrap-v-select sp-split__select">
          <span class="v-select-label">{{ $t("shippingPlan.split.arrivalDateType") }}</span>
          <v-select
            class="custom-select"
            label="name"
            :options="arrivalDateTypeOptions"
            :reduce="(o) => o.id"
            :value="col.arrivalDateType"
            :append-to-body="true"
            @input="col.arrivalDateType = $event"
          ></v-select>
        </div>

        <!-- Artikelpositionen: Kopien starten bewusst mit Menge 0 -->
        <h5>{{ $t("shippingPlan.product") }}</h5>
        <table class="sp-split__items">
          <tr v-for="item in col.products" :key="item.uid">
            <td>{{ item.sku }}</td>
            <td><input v-model.number="item.quantityDelivering" type="number" min="0" step="1" :class="{ 'sp-split__qty--mismatch': mismatchSkus.has(item.sku) }" /></td>
          </tr>
        </table>
      </section>
    </div>

    <!-- Mengen-Kontrolle: verteilt vs. Ursprungsmenge -->
    <div v-if="quantityCheck.length" class="sp-split__totals">
      <span v-for="q in quantityCheck" :key="q.sku" class="sp-split__total" :class="{ 'sp-split__total--mismatch': q.distributed !== q.original }" :title="$t('shippingPlan.split.distributed')">
        {{ q.sku }}: {{ q.distributed }}/{{ q.original }}
      </span>
    </div>

    <div class="sp-split__actions">
      <md-button class="md-info" @click="addColumn"> <md-icon md-src="/img/arrow_split.svg" />&nbsp;{{ $t("shippingPlan.split.addContainer") }} </md-button>
      <div>
        <md-button class="md-secondary mr-1" @click="$emit('close')">{{ $t("shippingPlan.cancel") }}</md-button>
        <md-button class="md-success" :disabled="saving || columns.length < 2" @click="save">{{ $t("shippingPlan.save") }}</md-button>
      </div>
    </div>
  </div>
</template>

<script>
import axiosFactory from "../../services/axios.factory";
import CSLoading from "../Loading/CSLoading.vue";
import { normalizeStatusName } from "../../mixins/formats";
import { UserAvatar } from "@/components";
import DatePicker from "vue2-datepicker";
import { DateTimePicker } from "@lazy-copilot/datetimepicker";

// Containernummern-Muster (ISO 6346: 4 Buchstaben + 7 Ziffern), fuer die Vorbelegung beim
// Aufteilen eines Sammelfelds wie "TGHU1234567 / MSKU7654321"
const CONTAINER_NO = /[A-Z]{4}\s?\d{7}/g;

export default {
  name: "ShippingPlanSplit",
  components: { CSLoading, UserAvatar, DatePicker, DateTimePicker },
  props: {
    source: { type: Object, required: true },
    options: { type: Object, required: true },
  },
  data() {
    return {
      saving: false,
      uidSeq: 0,
      columns: [],
      // Buchungs-/MTC-/CO-Status werden bewusst 1:1 kopiert und sind hier nicht aenderbar -
      // dafuer gibt es den Bearbeiten-Dialog
      fields: [
        { key: "shipmentRecordName", type: "text", label: "shippingPlan.shipmentRecordName" },
        { key: "recordStatusId", type: "select", options: "recordStatusOptions", label: "shippingPlan.recordStatus", i18n: "shippingPlan.statuses.recordStatuses" },
        { key: "recordTags", type: "text", label: "shippingPlan.filterByTag" },
        { key: "containerId", type: "text", label: "shippingPlan.containerId" },
        { key: "containerSizeId", type: "select", options: "containerSizeOptions", label: "shippingPlan.containerSize", i18n: "shippingPlan.statuses.containerSizes" },
        { key: "containerArrivalDateInput", type: "datetime", label: "shippingPlan.containerArrivalDate" },
        { key: "containerArrivalPlaceId", type: "select", options: "containerArrivalPlaceOptions", label: "shippingPlan.containerArrivalPlace" },
        { key: "carrierNameId", type: "select", options: "carrierNameOptions", label: "shippingPlan.carrierName" },
        { key: "containerBookingDateInput", type: "date", label: "shippingPlan.containerBookingDate" },
        { key: "carrierShipmentId", type: "text", label: "shippingPlan.carrierShipmentId" },
        { key: "trackingUrl", type: "text", label: "shippingPlan.trackingUrl" },
        { key: "shipmentNote", type: "textarea", label: "shippingPlan.shipmentNote" },
        { key: "productDataSent", type: "bool", label: "shippingPlan.productDataSent" },
        { key: "supplierOrderId", type: "text", label: "shippingPlan.supplierOrderId" },
        { key: "supplierNameId", type: "select", options: "supplierNameOptions", label: "shippingPlan.supplierName" },
        { key: "paymentStatusId", type: "mdselect", options: "paymentStatusOptions", label: "shippingPlan.paymentStatus", i18n: "shippingPlan.statuses.paymentStatuses" },
        { key: "orderTotalAmount", type: "money", label: "shippingPlan.orderTotalAmount" },
        { key: "prepaymentAmount", type: "money", label: "shippingPlan.prepaymentAmount" },
        { key: "prepaymentDateInput", type: "date", label: "shippingPlan.prepaymentDate" },
        { key: "fullPaidDateInput", type: "date", label: "shippingPlan.fullPaidDate" },
        { key: "exchangeRate", type: "number", label: "shippingPlan.exchangeRate" },
        { key: "shipmentPaidStatusId", type: "mdselect", options: "shipmentPaidStatusOptions", label: "shippingPlan.shipmentPaidStatus", i18n: "shippingPlan.statuses.shipmentPaidStatuses" },
        { key: "shipmentDocumentNumber", type: "text", label: "shippingPlan.shipmentDocumentNumber" },
        { key: "shipmentAmount", type: "money", label: "shippingPlan.shipmentAmount" },
        { key: "customsPaidStatusId", type: "mdselect", options: "customsPaidStatusOptions", label: "shippingPlan.customsPaidStatus", i18n: "shippingPlan.statuses.customsPaidStatuses" },
        { key: "customsDocumentNumber", type: "text", label: "shippingPlan.customsDocumentNumber" },
        { key: "customsAmount", type: "money", label: "shippingPlan.customsAmount" },
        { key: "chinaUser", type: "select", options: "chinaUserOptions", label: "shippingPlan.chinaUser" },
        { key: "chinaStatusId", type: "mdselect", options: "chinaStatusOptions", label: "shippingPlan.chinaStatus", i18n: "shippingPlan.statuses.chinaStatuses" },
        { key: "chinaText", type: "textarea", label: "shippingPlan.chinaText" },
        { key: "accountingStatusId", type: "mdselect", options: "accountingStatusOptions", label: "shippingPlan.accountingStatus", i18n: "shippingPlan.statuses.accountingStatuses" },
        { key: "accountingStatusDateInput", type: "date", label: "shippingPlan.accountingStatusDate" },
      ],
    };
  },
  computed: {
    arrivalDateTypeOptions() {
      return [
        { id: "fixed", name: this.$t("shippingPlan.dateIsFixed") },
        { id: "planned", name: this.$t("shippingPlan.planned") },
        { id: "provisional", name: this.$t("shippingPlan.provisional") },
      ];
    },
    quantityCheck() {
      const original = {};
      (this.source.products || []).forEach((p) => {
        original[p.sku] = (original[p.sku] || 0) + (p.quantityDelivering || 0);
      });
      const distributed = {};
      this.columns.forEach((col) => {
        col.products.forEach((p) => {
          distributed[p.sku] = (distributed[p.sku] || 0) + (Number(p.quantityDelivering) || 0);
        });
      });
      return Object.keys(original).map((sku) => ({ sku, original: original[sku], distributed: distributed[sku] || 0 }));
    },
    quantityMismatch() {
      return this.quantityCheck.some((q) => q.distributed !== q.original);
    },
    // SKUs mit abweichender Summe: rote Umrandung direkt am Mengen-Input (Kundenwunsch 21.08.,
    // Speichern bleibt erlaubt - es kommt oefter mehr an als urspruenglich geplant)
    mismatchSkus() {
      return new Set(this.quantityCheck.filter((q) => q.distributed !== q.original).map((q) => q.sku));
    },
  },
  created() {
    this.columns = [this.makeColumn(true)];
    // Vorbelegung: stecken mehrere Containernummern im Feld, direkt je Nummer eine Spalte
    const numbers = String(this.source.containerId || "").match(CONTAINER_NO) || [];
    if (numbers.length > 1) {
      this.columns[0].containerId = numbers[0];
      for (let i = 1; i < numbers.length; i++) {
        const col = this.makeColumn(false);
        col.containerId = numbers[i];
        this.columns.push(col);
      }
    }
  },
  methods: {
    // Statuslisten kommen englisch aus der DB - dieselbe Uebersetzung wie im Bearbeiten-Dialog.
    // Felder ohne i18n-Gruppe (Lieferant, Anlieferstelle, Logistiker, China-User) bleiben Klartext.
    optionLabel(field, option) {
      if (!option) return "";
      if (!field.i18n) return option.name;
      return this.$t(`${field.i18n}.${normalizeStatusName(option.name)}`);
    },
    toDateInput(value) {
      return value ? String(value).slice(0, 10) : null;
    },
    makeColumn(isOrigin) {
      const s = this.source;
      return {
        shipmentRecordName: s.shipmentRecordName,
        recordStatusId: s.recordStatusId,
        recordTags: s.recordTags || null,
        containerId: s.containerId,
        containerSizeId: s.containerSizeId,
        containerArrivalDateInput: s.containerArrivalDate ? new Date(s.containerArrivalDate) : null,
        arrivalDateType: s.containerArrivalDateFixed ? "fixed" : s.containerArrivalDatePlanned ? "planned" : s.containerArrivalDateProvisional ? "provisional" : null,
        containerArrivalPlaceId: s.containerArrivalPlaceId,
        carrierNameId: s.carrierNameId,
        containerBookingStatus: s.containerBookingStatus || "pending",
        containerBookingDateInput: this.toDateInput(s.containerBookingDate),
        mtcStatus: s.mtcStatus || "pending",
        coStatus: s.coStatus || "pending",
        carrierShipmentId: s.carrierShipmentId,
        trackingUrl: s.trackingUrl,
        shipmentNote: s.shipmentNote,
        productDataSent: !!s.productDataSent,
        supplierOrderId: s.supplierOrderId,
        supplierNameId: s.supplierNameId,
        paymentStatusId: s.paymentStatusId,
        orderTotalAmount: this.formatMoney(s.orderTotalAmount),
        prepaymentAmount: this.formatMoney(s.prepaymentAmount),
        prepaymentDateInput: this.toDateInput(s.prepaymentDate),
        fullPaidDateInput: this.toDateInput(s.fullpaidDate || s.fullPaidDate),
        exchangeRate: this.formatRate(s.exchangeRate),
        shipmentPaidStatusId: s.shipmentPaidStatusId,
        shipmentDocumentNumber: s.shipmentDocumentNumber,
        shipmentAmount: this.formatMoney(s.shipmentAmount),
        customsPaidStatusId: s.customsPaidStatusId,
        customsDocumentNumber: s.customsDocumentNumber,
        customsAmount: this.formatMoney(s.customsAmount),
        chinaUser: s.chinaUser,
        chinaStatusId: s.chinaStatusId,
        chinaText: s.chinaText,
        accountingStatusId: s.accountingStatusId,
        accountingStatusDateInput: this.toDateInput(s.accountingStatusDate),
        products: (s.products || []).map((p) => ({
          uid: `c-${++this.uidSeq}`,
          linkedProductId: isOrigin ? p.linkedProductId || 0 : 0,
          id: p.id,
          sku: p.sku,
          // Kopien starten mit Menge 0: bewusste Eingabe statt versehentlicher Verdopplung
          quantityDelivering: isOrigin ? p.quantityDelivering : 0,
          purchasePrice: p.purchasePrice,
          position: p.position || 0,
        })),
      };
    },
    addColumn() {
      // "Immer wieder mit den Informationen des Ursprungscontainers" - Basis ist Spalte 1
      const col = this.makeColumn(false);
      const numbers = String(this.columns[0].containerId || "").match(CONTAINER_NO) || [];
      col.containerId = numbers.length === 1 ? null : col.containerId;
      this.columns.push(col);
    },
    removeColumn(index) {
      this.columns.splice(index, 1);
    },
    toPayload(col, isOrigin) {
      return {
        shipmentRecordId: isOrigin ? this.source.shipmentRecordId : 0,
        recordStatusId: col.recordStatusId,
        recordTags: col.recordTags || null,
        shipmentNote: col.shipmentNote,
        shipmentRecordName: col.shipmentRecordName,
        supplierOrderId: col.supplierOrderId,
        supplierNameId: col.supplierNameId,
        containerId: col.containerId,
        containerSizeId: col.containerSizeId,
        containerArrivalDate: col.containerArrivalDateInput ? new Date(col.containerArrivalDateInput).toISOString() : null,
        containerArrivalDateFixed: col.arrivalDateType === "fixed",
        containerArrivalDatePlanned: col.arrivalDateType === "planned",
        containerArrivalDateProvisional: col.arrivalDateType === "provisional",
        containerArrivalPlaceId: col.containerArrivalPlaceId,
        productDataSent: !!col.productDataSent,
        containerBookingStatus: col.containerBookingStatus,
        containerBookingDate: col.containerBookingDateInput || null,
        mtcStatus: col.mtcStatus,
        coStatus: col.coStatus,
        carrierShipmentId: col.carrierShipmentId,
        trackingUrl: col.trackingUrl,
        carrierNameId: col.carrierNameId,
        paymentStatusId: col.paymentStatusId,
        orderTotalAmount: this.parseMoney(col.orderTotalAmount),
        prepaymentAmount: this.parseMoney(col.prepaymentAmount),
        prepaymentDate: col.prepaymentDateInput || null,
        fullPaidDate: col.fullPaidDateInput || null,
        shipmentPaidStatusId: col.shipmentPaidStatusId,
        shipmentDocumentNumber: col.shipmentDocumentNumber,
        shipmentAmount: this.parseMoney(col.shipmentAmount),
        customsPaidStatusId: col.customsPaidStatusId,
        customsDocumentNumber: col.customsDocumentNumber,
        customsAmount: this.parseMoney(col.customsAmount),
        chinaStatusId: col.chinaStatusId,
        chinaText: col.chinaText,
        chinaUser: col.chinaUser,
        accountingStatusId: col.accountingStatusId,
        accountingStatusDate: col.accountingStatusDateInput || null,
        exchangeRate: this.parseMoney(col.exchangeRate),
        linkedProducts: col.products
          .filter((p) => p.id > 0 && Number(p.quantityDelivering) > 0)
          .map((p, index) => ({
            linkedProductId: p.linkedProductId || 0,
            id: p.id,
            name: p.sku,
            sku: p.sku,
            quantityDelivering: Number(p.quantityDelivering) || 0,
            purchasePrice: p.purchasePrice,
            position: index,
          })),
      };
    },
    formatMoney(val) {
      if (val === null || val === undefined || val === "") return null;
      const locale = this.$i18n.locale === "de" ? "de-DE" : "en-GB";
      return Number(val).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true });
    },
    formatRate(val) {
      if (val === null || val === undefined || val === "") return null;
      const locale = this.$i18n.locale === "de" ? "de-DE" : "en-GB";
      return Number(val).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 4, useGrouping: false });
    },
    parseMoney(val) {
      if (val === null || val === undefined || val === "") return null;
      const str = this.$i18n.locale === "de" ? String(val).replace(/\./g, "").replace(",", ".") : String(val).replace(/,/g, "");
      const n = parseFloat(str);
      return isNaN(n) ? null : n;
    },
    async save() {
      this.saving = true;
      try {
        const payload = this.columns.map((col, index) => this.toPayload(col, index === 0));
        await axiosFactory().post(`/shipping_plans/${this.source.shipmentRecordId}/split`, payload);
        this.$emit("saved", this.columns.length);
      } catch (e) {
        const reason = e && e.response && e.response.data && e.response.data.reason;
        this.$notify({
          timeout: 4000,
          message: this.$t(reason === "already_booked" ? "shippingPlan.split.errors.already_booked" : "shippingPlan.split.errors.failed"),
          icon: "add_alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sp-split__date {
  width: 100%;
  margin-bottom: 16px;
}
.sp-split__datetime {
  margin-bottom: 16px;
}
.sp-split__option-logo {
  height: 18px;
  width: auto;
  vertical-align: middle;
  margin-right: 6px;
}
.sp-split__option-avatar {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  vertical-align: middle;
  margin-right: 6px;
  object-fit: cover;
}
.sp-split {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px 24px 24px;
}
.sp-split__hints {
  flex: 0 0 auto;
  p {
    margin: 2px 0;
    font-size: 13px;
    color: #666;
    .md-icon {
      font-size: 16px !important;
      color: #2196f3;
    }
  }
}
.sp-split__scroll {
  flex: 1 1 auto;
  overflow: auto;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding-bottom: 8px;
}
.sp-split__col {
  flex: 0 0 340px;
  max-width: 340px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 4px 16px 16px;
  h4 {
    margin: 8px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}
.sp-split__col--origin {
  border-color: #7f915a;
  background: #f7fbf7;
}
.sp-split__remove {
  margin: 0;
}
.sp-split__select {
  text-align: left;
  margin-bottom: 12px;
}
.sp-split__items {
  width: 100%;
  td {
    padding: 2px 4px;
    border-bottom: 1px solid #eee;
  }
  input {
    width: 70px;
    border: 0;
    border-bottom: 1px solid #ccc;
    text-align: right;
    padding: 2px 0;
    &:focus {
      outline: none;
      border-bottom-color: #9c27b0;
    }
  }
}
.sp-split__totals {
  flex: 0 0 auto;
  padding: 8px 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  font-size: 13px;
}
.sp-split__total--mismatch {
  color: #b71c1c;
  font-weight: 600;
}
.sp-split__actions {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e0e0e0;
  padding-top: 8px;
  margin-top: 8px;
}

.sp-split__qty--mismatch {
  border: 2px solid #f44336 !important;
  border-radius: 4px;
  outline: none;
}
</style>
